// Models
import { IExerciseData } from 'storage/exercise/models'
import { IWorkoutSetInput } from 'models'

// React
import {
  ChangeEvent,
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'

// Libraries
import { ThemeContext } from 'styled-components'
import { useFormContext } from 'react-hook-form'

// Misc
import useMediaQuery from 'hooks/useMediaQuery'

// Components
import * as Styled from './styled'
import { Aligner, InputCheckbox, InputText } from 'heeds-ds'
import { Exercise } from 'components'

type Props = {
  displayMode?: boolean
  exercise: IExerciseData
  name: string
  onClickImage?: (exercise: IExerciseData) => void
  onRemove?: () => void
  readonly?: boolean
  replicateWorkoutSetFields?: (set: IWorkoutSetInput) => void
  showBorder?: boolean
  visible?: boolean
}

const SeriesFormTag: FC<Props> = ({
  displayMode,
  exercise,
  name,
  onClickImage,
  onRemove,
  readonly,
  replicateWorkoutSetFields,
  showBorder,
  visible,
}) => {
  const { setValue, getFieldState, unregister, watch, resetField } =
    useFormContext()
  const theme = useContext(ThemeContext)
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.desktop}px)`)

  const [isHovered, setIsHovered] = useState(false)
  const [show, setShow] = useState(false)

  const headerMargin = show ? (isDesktop ? '0 0 16px 0' : '0 0 24px 0') : '0'

  const { isDirty: someFieldIsDirty } = getFieldState(name)
  const { isDirty: repeatFieldIsDirty } = getFieldState(`${name}.repeat`)

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault()
    !isDesktop && setShow((prev) => !prev)
  }

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(`${name}.repeat`, event.target.checked)
    if (event.target.checked) {
      resetField(name, { defaultValue: watch(name) })
      const set = watch(name) as IWorkoutSetInput
      replicateWorkoutSetFields?.(set)
    }
  }

  useEffect(() => {
    if (someFieldIsDirty && !repeatFieldIsDirty) {
      setValue(`${name}.repeat`, false)
    }
  }, [someFieldIsDirty, name, repeatFieldIsDirty, setValue])

  useLayoutEffect(() => {
    isDesktop && setShow(true)
  }, [isDesktop])

  return (
    <Styled.Container
      data-testid="series-form-tag-container"
      hovered={isHovered}
      onMouseEnter={() => !readonly && setIsHovered(true)}
      onMouseLeave={() => !readonly && setIsHovered(false)}
      showBorder={showBorder}
      visible={visible}
    >
      <Aligner flex="column">
        <Styled.Header
          data-testid="series-form-tag-header"
          margin={headerMargin}
          onClick={handleClick}
        >
          <Exercise
            active={!isDesktop}
            displayMode={displayMode}
            exercise={exercise}
            margin="0 auto 0 0"
            onClick={() => onRemove?.()}
            onClickImage={onClickImage}
            padding="0"
            transparent
          />

          {isDesktop && !readonly && isHovered && onRemove && (
            <Styled.HiddenButton
              data-testid="series-form-tag-remove-button"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                unregister(name)
                onRemove()
              }}
            >
              <Styled.DeleteIcon />
            </Styled.HiddenButton>
          )}
        </Styled.Header>

        <Styled.ContentInputs
          data-testid="series-form-tag-inputs"
          visible={show}
        >
          <Styled.RowOne>
            <Styled.Input
              label="Série"
              name={`${name}.quantity`}
              placeholder="0"
              readOnly={readonly}
              mask="integer"
            />

            <Styled.Input
              label="Repetição"
              name={`${name}.exercises.0.repetitions`}
              placeholder="0 - 0"
              readOnly={readonly}
              mask="repetitions"
            />

            <Styled.Input
              label="Intervalo"
              name={`${name}.interval`}
              placeholder="0 seg"
              readOnly={readonly}
              mask="seconds"
            />
          </Styled.RowOne>

          <Styled.RowTwo>
            <Styled.Input
              name={`${name}.exercises.0.weight`}
              label="Carga"
              placeholder="0 kg"
              readOnly={readonly}
              mask="weight"
            />

            <Styled.Input
              name={`${name}.exercises.0.cadence`}
              label="Cadência"
              placeholder="0000"
              mask="cadence"
              readOnly={readonly}
            />
          </Styled.RowTwo>

          {!readonly && (
            <Styled.Label>
              <InputCheckbox
                checked={Boolean(watch(`${name}.repeat`))}
                onChange={handleCheckboxChange}
              />
              Repetir para todos os exercícios
            </Styled.Label>
          )}
        </Styled.ContentInputs>

        <Styled.ContentComment visible={show}>
          <InputText
            label="Comentários sobre a Série"
            margin="0"
            name={`${name}.comments`}
            placeholder="Esse espaço tem como função passar orientações para o seu aluno."
            scale="small"
            readOnly={readonly}
          />
        </Styled.ContentComment>
      </Aligner>
    </Styled.Container>
  )
}

export default SeriesFormTag
